import { useEffect, useRef, useState } from "react";
import { Sparkles, Plus, Check, PenLine } from "lucide-react";
import { Spinner } from "./Brand";
import { NoteHeader } from "./AiNote";
import { api } from "@/lib/api";
import { CORE_NEEDS, needLabel } from "@/lib/schemaFlip";
import { NEED_UNSURE } from "./NeedPicker";

const OPTIONS = CORE_NEEDS.flatMap((n) => n.items.map((i) => ({ key: `${n.key}:${i.need}`, says: i.says })));

async function suggest(context, ownWords) {
  const { data } = await api.post("/ai/suggest-needs", { options: OPTIONS, context, ownWords }, { timeout: 60000 });
  return data;
}

// AI help for the core-needs step: auto-suggest from the trigger, or map the user's own words to needs.
export function NeedAssistant({ context, value, onChange, autoSuggest = true }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [words, setWords] = useState("");
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const run = async (own = "") => {
    setLoading(true);
    setFailed(false);
    try {
      const r = await suggest(context, own);
      setResult(r.needs.length ? { ...r, own: !!own } : null);
      setFailed(!r.needs.length);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!autoSuggest || started.current) return;
    started.current = true;
    run("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSuggest]);

  const add = (k) => onChange(value.includes(k) ? value : [...value.filter((v) => v !== NEED_UNSURE), k]);
  const addAll = () => onChange([...new Set([...value.filter((v) => v !== NEED_UNSURE), ...result.needs])]);

  return (
    <div className="mt-6 flex flex-col gap-4" data-testid="need-assistant">
      <div className="rounded-[20px] bg-t-brand3 border border-t-brand2 p-5" data-testid="need-ai-suggestions">
        <NoteHeader title={<p className="flex items-center gap-2 font-semibold text-t-onbrand3"><Sparkles size={16} /> {result?.own ? "What your words point to" : "Needs that may be underneath this"}</p>} text={!loading && result?.message ? result.message : null} testId="need-ai-listen" />
        {loading ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-t-on2"><Spinner className="w-4 h-4 text-t-brand" /> Gently considering what you shared…</div>
        ) : result ? (
          <>
            {result.message ? <p className="mt-3 text-t-on leading-relaxed whitespace-pre-line" data-testid="need-ai-message">{result.message}</p> : null}
            <div className="mt-3 flex flex-col gap-2">
              {result.needs.map((k, i) => {
                const on = value.includes(k);
                return (
                  <button key={k} type="button" onClick={() => add(k)} disabled={on} data-testid={`need-suggestion-${i}`}
                    className={`flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${on ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface text-t-on border-t-border hover:border-t-brand"}`}>
                    {on ? <Check size={16} strokeWidth={3} /> : <Plus size={16} className="text-t-brand" />}
                    <span className="text-[15px]">{needLabel(k)}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button type="button" onClick={addAll} className="rounded-full bg-t-brand text-t-onbrand px-4 h-9 text-sm font-semibold" data-testid="need-suggestions-add-all">Add all suggestions</button>
            </div>
            <p className="mt-2 text-xs text-t-muted">These are gentle suggestions — only keep what feels true for you.</p>
          </>
        ) : (
          <p className="mt-2 text-sm text-t-on2" data-testid="need-ai-empty">
            {failed ? "I couldn't find suggestions just now — try describing your needs in your own words below, or tick them yourself." : "Describe what you need in your own words below and I'll help you name it."}
          </p>
        )}
      </div>

      <div className="tf-card p-5">
        <p className="flex items-center gap-2 font-semibold text-t-on"><PenLine size={16} className="text-t-brand" /> In your own words</p>
        <p className="mt-1 text-sm text-t-muted">What do you wish you'd had in that moment — from others, or from yourself?</p>
        <textarea className="tf-input mt-3" value={words} onChange={(e) => setWords(e.target.value)} maxLength={2000}
          placeholder="e.g. I just wanted someone to check on me and say it was okay to feel this way…" data-testid="need-own-words-input" />
        <button type="button" onClick={() => run(words.trim())} disabled={!words.trim() || loading} data-testid="need-own-words-button"
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-t-brand text-t-onbrand px-5 h-11 font-semibold disabled:opacity-50">
          {loading ? <Spinner className="w-4 h-4" /> : <Sparkles size={16} />} Help me find my needs
        </button>
      </div>
      <p className="tf-tag mt-2">Or explore all core needs</p>
    </div>
  );
}
